import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Stack,
  Button,
  TextField,
  Grid,
  Checkbox,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Divider
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Search, PictureAsPdf, TableView, Save, Print, Edit, DriveFileMove } from '@mui/icons-material';
import { authGet, authGetBlob, authPatch, authPost, authPostBlob, authPut } from '../../lib/api';
import {
  buildRosterWorkbook,
  downloadRosterWorkbook,
  dwiInterventionAttendanceColumns,
  exportableRosterColumns,
} from './rosterExcel';
import { combinedCauseNumber } from './rosterCaseCause';
import SavedColumnViewControls, { useSavedColumnView } from '../../components/SavedColumnViewControls';
import { dateOnly, formatDate, todayDate } from '../../lib/dateFormat';
import { referralSourceOptionsFor } from '../../lib/referralSources';
import { exitInterviewFormForDialog } from './exitInterviewForm';
import { moveStudentDestinationClasses } from './moveStudentDestinations';

function formatTimeLabel(time24) {
  if (!time24) return '';
  const [hourStr, minute] = String(time24).slice(0, 5).split(':');
  const hour = Number(hourStr);
  const suffix = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour % 12 === 0 ? 12 : hour % 12;
  return `${hour12}:${minute} ${suffix}`;
}

function formatDateLabel(value) {
  return formatDate(value);
}

function currentMonthDateRange() {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return {
    startDate: dateOnly(start),
    endDate: dateOnly(end)
  };
}

const STATUS_FILTER_OPTIONS = [
  { value: 'open', label: 'Open' },
  { value: 'locked', label: 'Locked' },
  { value: 'archived', label: 'Archived' }
];

function defaultRosterFilters() {
  const { startDate, endDate } = currentMonthDateRange();
  return {
    q: '',
    instructor: '',
    classType: '',
    startDate,
    endDate,
    status: ['open']
  };
}

function formatPhone(value) {
  const digits = String(value || '').replace(/\D/g, '').slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

const emptyExitInterviewForm = {
  exit_interview_program: '',
  exit_interview_completion_date: '',
  exit_interview_participation_rating: 'Good',
  exit_interview_recommendations: '',
  exit_interview_evaluation_instrument: '',
  exit_interview_evaluation_result: '',
  exit_interview_evaluation_narrative: '',
  exit_interview_instructor: '',
  exit_interview_program_certification_number: '742',
  exit_interview_print_date: ''
};

const emptyStudentForm = {
  code: '',
  ssn: '',
  first_name: '',
  middle_name: '',
  last_name: '',
  birth_date: '',
  phone: '',
  email: '',
  address: '',
  city: '',
  state: '',
  zip: '',
  driver_number: '',
  cause_number: '',
  county_of_conviction: '',
  probation_officer_name: '',
  probation_officer_email: '',
  probation_officer_phone: '',
  attorney_name: '',
  attorney_email: '',
  attorney_phone: '',
  reporting_agency: '',
  referral_source: '',
  state_reporting_notes: '',
  call_notes: ''
};

function studentToForm(student = {}) {
  return {
    ...emptyStudentForm,
    ...student,
    birth_date: dateOnly(student.birth_date),
    ssn: String(student.ssn_last4 || student.ssn || '').replace(/\D/g, '').slice(-4)
  };
}

function studentPayloadFromForm(form = {}) {
  return {
    ...form,
    ssn: String(form.ssn || '').replace(/\D/g, '').slice(0, 4),
    cause_number: form.cause_number || '',
    phone: formatPhone(form.phone),
    probation_officer_phone: formatPhone(form.probation_officer_phone),
    attorney_phone: formatPhone(form.attorney_phone)
  };
}

function normalizeClassCode(value) {
  return String(value || '').trim().toUpperCase().replace(/[^A-Z0-9]/g, '');
}

function classHaystack(scheduledClass = {}) {
  const row = scheduledClass || {};
  return `${row.class_code || ''} ${row.class_name || ''} ${row.class_description || ''}`.toUpperCase();
}

function getRosterProgramType(scheduledClass = {}) {
  const row = scheduledClass || {};
  const code = normalizeClassCode(row.class_code);
  const haystack = classHaystack(row);

  if (code === 'DWII' || haystack.includes('DWI INTERVENTION') || haystack.includes('REPEAT OFFENDER')) return 'DWII';
  if (code === 'DOEP' || haystack.includes('DRUG OFFENDER')) return 'DOEP';
  if (code === 'VIP' || haystack.includes('VICTIM IMPACT')) return 'VIP';
  if (code === 'DWI101' || code === 'DWIE' || haystack.includes('DWI-101') || haystack.includes('DWI 101') || haystack.includes('DWI EDUCATION')) return 'DWI101';
  return 'DEFAULT';
}

function scoreColumnLabelForClass(scheduledClass = {}) {
  const programType = getRosterProgramType(scheduledClass);
  if (programType === 'DWI101') return 'NDP Score';
  if (programType === 'DWII') return 'SASSI';
  return 'DAST Score';
}

function isCertificateEligibleClass(scheduledClass) {
  if (!scheduledClass) return false;
  const code = normalizeClassCode(scheduledClass.class_code);
  const haystack = classHaystack(scheduledClass);

  return ['DOEP', 'DWIE', 'DWII'].includes(code)
    || haystack.includes('DRUG OFFENDER')
    || haystack.includes('DWI EDUCATION')
    || haystack.includes('DWI INTERVENTION')
    || haystack.includes('REPEAT OFFENDER');
}

function hasClassDatePassed(scheduledClass = {}) {
  const endDate = scheduledClass.end_date || scheduledClass.class_date;
  if (!endDate) return false;

  const classEnd = new Date(`${String(endDate).slice(0, 10)}T23:59:59`);
  if (Number.isNaN(classEnd.getTime())) return false;

  return classEnd < new Date();
}

function requiresAdminOverrideForRoster(scheduledClass = {}) {
  const status = String(scheduledClass?.status || 'open').toLowerCase();
  if (status === 'archived') return true;
  if (status === 'locked') return hasClassDatePassed(scheduledClass);
  return false;
}

export default function RostersPage() {
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState(null);
  const [attendanceDays, setAttendanceDays] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);

  const [filters, setFilters] = useState(defaultRosterFilters);

  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedCertificateStudentIds, setSelectedCertificateStudentIds] = useState([]);
  const [adminOverride, setAdminOverride] = useState(false);
  const [scheduledClasses, setScheduledClasses] = useState([]);
  const [filterOptions, setFilterOptions] = useState({ classTypes: [], instructors: [] });
  const [transferDialog, setTransferDialog] = useState(null);
  const [studentEditDialog, setStudentEditDialog] = useState(null);
  const [studentEditForm, setStudentEditForm] = useState(emptyStudentForm);
  const [studentEditLoading, setStudentEditLoading] = useState(false);
  const [saveAllLoading, setSaveAllLoading] = useState(false);
  const [exitInterviewDialog, setExitInterviewDialog] = useState(null);
  const [exitInterviewForm, setExitInterviewForm] = useState(emptyExitInterviewForm);
  const [exitInterviewLoading, setExitInterviewLoading] = useState(false);

  const loadClasses = useCallback(async (activeFilters = filters) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(activeFilters).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          if (value.length) params.set(key, value.join(','));
        } else if (value) {
          params.set(key, value);
        }
      });

      const data = await authGet(`/api/rosters/classes?${params.toString()}`);
      setClasses(data.classes || []);
    } catch (err) {
      console.error('Load roster classes error:', err);
      setClasses([]);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const loadScheduledClasses = useCallback(async () => {
    try {
      const data = await authGet('/api/scheduled-classes');
      setScheduledClasses(data.scheduledClasses || []);
    } catch (err) {
      console.error('Load scheduled classes error:', err);
      setScheduledClasses([]);
    }
  }, []);

  const loadFilterOptions = useCallback(async () => {
    try {
      const data = await authGet('/api/rosters/filter-options');
      setFilterOptions({
        classTypes: data.classTypes || [],
        instructors: data.instructors || []
      });
    } catch (err) {
      console.error('Load roster filter options error:', err);
      setFilterOptions({ classTypes: [], instructors: [] });
    }
  }, []);

  const loadRoster = async (scheduledClassId) => {
    setDetailLoading(true);
    try {
      const data = await authGet(`/api/rosters/${scheduledClassId}`);
      setSelectedClass(data.scheduledClass);
      setAttendanceDays(data.attendanceDays || []);
      setStudents(data.students || []);
      setSelectedCertificateStudentIds((data.students || []).map((student) => student.student_id));
      setAdminOverride(false);
      setDetailOpen(true);
    } catch (err) {
      console.error('Load roster detail error:', err);
    } finally {
      setDetailLoading(false);
    }
  };

  useEffect(() => {
    loadClasses();
    loadScheduledClasses();
    loadFilterOptions();
  }, [loadClasses, loadScheduledClasses, loadFilterOptions]);

  const updateStudentLocal = (studentId, patch) => {
    setStudents((prev) =>
      prev.map((student) =>
        student.student_id === studentId ? { ...student, ...patch } : student
      )
    );
  };

  const openExitInterview = async (student) => {
    if (!selectedClass) return;
    setExitInterviewLoading(true);
    try {
      const data = await authGet(`/api/rosters/${selectedClass.id}/students/${student.student_id}/exit-interview`);
      setExitInterviewForm(exitInterviewFormForDialog(
        emptyExitInterviewForm,
        data.exitInterview,
        selectedClass.class_name || ''
      ));
      setExitInterviewDialog(student);
    } catch (err) {
      console.error('Load Exit Interview error:', err);
    } finally {
      setExitInterviewLoading(false);
    }
  };

  const saveExitInterview = async () => {
    if (!selectedClass || !exitInterviewDialog) return;
    setExitInterviewLoading(true);
    try {
      await authPut(`/api/rosters/${selectedClass.id}/students/${exitInterviewDialog.student_id}/exit-interview`, {
        ...exitInterviewForm,
        admin_override: adminOverride
      });
      setExitInterviewDialog(null);
    } catch (err) {
      console.error('Save Exit Interview error:', err);
    } finally {
      setExitInterviewLoading(false);
    }
  };

  const printExitInterview = async (student) => {
    if (!selectedClass) return;
    try {
      const blob = await authGetBlob(`/api/rosters/${selectedClass.id}/students/${student.student_id}/exit-interview.pdf`);
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      console.error('Print Exit Interview error:', err);
    }
  };


  const classStatus = String(selectedClass?.status || 'open').toLowerCase();
  const classLocked = requiresAdminOverrideForRoster(selectedClass);
  const lockedWithoutOverride = classLocked && !adminOverride;

  const guardLockedClass = (action) => {
    if (!lockedWithoutOverride) return false;
    alert(`This class is ${classStatus}. Administrator override is required to ${action}.`);
    return true;
  };


  const transferDestinationClasses = useMemo(() => {
    if (!transferDialog?.sourceClassId) return [];
    return moveStudentDestinationClasses(scheduledClasses, {
      sourceClassId: transferDialog.sourceClassId,
      destinationType: transferDialog.destination_type,
      today: todayDate()
    });
  }, [scheduledClasses, transferDialog?.sourceClassId, transferDialog?.destination_type]);

  const selectedTransferDestinationClass = useMemo(
    () => scheduledClasses.find((item) => item.id === transferDialog?.destination_class_id) || null,
    [scheduledClasses, transferDialog?.destination_class_id]
  );

  const openTransferDialog = (student) => {
    if (!selectedClass?.id || !student?.student_id) return;
    if (guardLockedClass('move students')) return;
    setTransferDialog({
      sourceClassId: selectedClass.id,
      sourceClassName: selectedClass.class_name || selectedClass.class_description || 'this class',
      studentId: student.student_id,
      studentName: student.student_name || [student.first_name, student.last_name].filter(Boolean).join(' ') || 'this student',
      destination_type: 'class',
      destination_class_id: '',
      notes: ''
    });
  };

  const submitTransfer = async () => {
    if (!transferDialog?.sourceClassId || !transferDialog?.studentId) return;
    if (!transferDialog.destination_class_id) {
      alert(transferDialog.destination_type === 'holding_tank'
        ? 'Select a holding tank class.'
        : 'Select a destination class.');
      return;
    }

    const sourceClassId = transferDialog.sourceClassId;
    const destinationClassId = transferDialog.destination_class_id;

    try {
      const result = await authPost(`/api/rosters/${sourceClassId}/transfer`, {
        student_ids: [transferDialog.studentId],
        destination_type: transferDialog.destination_type,
        destination_class_id: destinationClassId,
        notes: transferDialog.notes || (selectedTransferDestinationClass?.class_name
          ? `Moved to ${selectedTransferDestinationClass.class_name}`
          : '')
      });
      setTransferDialog(null);
      alert(result.message || 'Student moved. Prior class history remains attached.');
      await Promise.all([loadScheduledClasses(), loadClasses()]);
      await loadRoster(destinationClassId || sourceClassId);
    } catch (err) {
      console.error('Transfer roster student error:', err);
      alert(err.message || 'Unable to move student');
    }
  };

  const openStudentEditDialog = async (student) => {
    if (!student?.student_id) return;
    setStudentEditLoading(true);
    setStudentEditDialog(student);
    setStudentEditForm(studentToForm(student));
    try {
      const data = await authGet(`/api/students/${student.student_id}`);
      setStudentEditDialog(data.student || student);
      setStudentEditForm(studentToForm(data.student || student));
    } catch (err) {
      console.error('Load student edit detail error:', err);
      alert(err.message || 'Unable to load full student record; showing roster fields only.');
    } finally {
      setStudentEditLoading(false);
    }
  };

  const saveStudentEdit = async () => {
    const studentId = studentEditDialog?.id || studentEditDialog?.student_id;
    if (!studentId) return;

    setStudentEditLoading(true);
    try {
      const data = await authPatch(`/api/students/${studentId}`, studentPayloadFromForm(studentEditForm));
      const updatedStudent = data.student || studentEditForm;
      const currentRosterStudent = students.find((student) => student.student_id === studentId);
      if (selectedClass?.id && currentRosterStudent) {
        await updateStudentRecord({
          ...currentRosterStudent,
          cause_number: updatedStudent.cause_number || '',
          county_of_conviction: updatedStudent.county_of_conviction || '',
          probation_officer_name: updatedStudent.probation_officer_name || '',
          probation_officer_email: updatedStudent.probation_officer_email || '',
          attorney_name: updatedStudent.attorney_name || '',
          attorney_email: updatedStudent.attorney_email || '',
          reporting_agency: updatedStudent.reporting_agency || '',
          referral_source: updatedStudent.referral_source || '',
          state_reporting_notes: updatedStudent.state_reporting_notes || ''
        }, { throwOnError: true });
        updateStudentLocal(studentId, { cause_number: updatedStudent.cause_number || '' });
      }
      await loadRoster(selectedClass.id);
      setStudentEditDialog(null);
      setStudentEditForm(emptyStudentForm);
      alert(`Student updated: ${[data.student?.first_name, data.student?.last_name].filter(Boolean).join(' ') || 'record saved'}`);
    } catch (err) {
      console.error('Save student edit error:', err);
      alert(err.message || 'Unable to save student');
    } finally {
      setStudentEditLoading(false);
    }
  };

  const updateStudentRecord = async (student, options = {}) => {
    if (!selectedClass?.id) return;
    if (guardLockedClass('change roster records or grades')) {
      if (options.throwOnError) throw new Error('Administrator override is required to save this roster.');
      return;
    }

    try {
      await authPatch(`/api/rosters/${selectedClass.id}/student/${student.student_id}`, {
        dast_code: student.dast_code || '',
        cause_number: student.cause_number || '',
        county_of_conviction: student.county_of_conviction || '',
        probation_officer_name: student.probation_officer_name || '',
        probation_officer_email: student.probation_officer_email || '',
        attorney_name: student.attorney_name || '',
        attorney_email: student.attorney_email || '',
        reporting_agency: student.reporting_agency || '',
        referral_source: student.referral_source || '',
        state_reporting_notes: student.state_reporting_notes || '',
        tx_cert_number: student.tx_cert_number || '',
        amount_due: student.amount_due || 0,
        pre_test_score: student.pre_test_score === '' ? null : student.pre_test_score,
        post_test_score: student.post_test_score === '' ? null : student.post_test_score,
        notes: student.notes || '',
        admin_override: adminOverride
      });
    } catch (err) {
      console.error('Update roster record error:', err);
      if (options.throwOnError) throw err;
      alert(err.message || 'Unable to save student roster row');
    }
  };

  const saveAllStudentRecords = async () => {
    if (!selectedClass?.id || !students.length) return;
    if (guardLockedClass('change roster records or grades')) return;

    setSaveAllLoading(true);
    const failures = [];
    try {
      for (const student of students) {
        try {
          await updateStudentRecord(student, { throwOnError: true });
        } catch (err) {
          const studentName = student.student_name || [student.first_name, student.last_name].filter(Boolean).join(' ') || student.student_id;
          failures.push(`${studentName}: ${err.message || 'Save failed'}`);
        }
      }

      if (failures.length) {
        alert(`Saved ${students.length - failures.length} of ${students.length} roster rows. Failed:\n\n${failures.slice(0, 8).join('\n')}${failures.length > 8 ? `\n...and ${failures.length - 8} more` : ''}`);
      } else {
        alert(`Saved all ${students.length} roster rows.`);
      }
      await loadRoster(selectedClass.id);
    } finally {
      setSaveAllLoading(false);
    }
  };

  const updateAttendance = async (student, day, attended) => {
    if (!selectedClass?.id) return;
    if (guardLockedClass('change attendance')) return;

    updateStudentLocal(student.student_id, {
      attendance: {
        ...(student.attendance || {}),
        [day]: attended
      }
    });

    try {
      await authPatch(`/api/rosters/${selectedClass.id}/student/${student.student_id}/attendance`, {
        attendance_date: day,
        attended,
        admin_override: adminOverride
      });
    } catch (err) {
      console.error('Update attendance error:', err);
      alert(err.message || 'Unable to update attendance');
    }
  };

  const exportPdf = async () => {
    if (!selectedClass?.id) return;

    try {
      const nonExportableRosterFields = new Set(['edit_action', 'move_action', 'save']);
      const classHiddenPrintFields = new Set([
        ...(hideTestScoreColumns ? ['pre_test_score', 'post_test_score'] : []),
        ...(hideCertificateNumberColumn ? ['tx_cert_number'] : []),
        ...(hideDastScoreColumn ? ['dast_code'] : [])
      ]);
      const exportFields = rosterColumns
        .filter((column) => column.field && !nonExportableRosterFields.has(column.field))
        .filter((column) => !classHiddenPrintFields.has(column.field))
        .filter((column) => rosterColumnVisibilityModel[column.field] !== false)
        .map((column) => column.field);
      const params = new URLSearchParams();
      if (exportFields.length) params.set('fields', exportFields.join(','));

      const html = await authGet(`/api/rosters/${selectedClass.id}/print?${params.toString()}`);

      const win = window.open('', '_blank');
      if (!win) {
        alert('Please allow popups to export the roster.');
        return;
      }

      win.document.open();
      win.document.write(html);
      win.document.close();
    } catch (err) {
      console.error('Export roster PDF error:', err);
      alert(err.message || 'Unable to export roster');
    }
  };

  const printCertificates = async () => {
    if (!selectedClass?.id) return;

    if (!isCertificateEligibleClass(selectedClass)) {
      alert('Certificates are only available for DOEP, DWIE, and DWII classes.');
      return;
    }

    if (!selectedCertificateStudentIds.length) {
      alert('Select at least one student to print.');
      return;
    }

    let win;

    try {
      win = window.open('', '_blank');
      if (!win) {
        alert('Please allow popups to print certificates.');
        return;
      }
      win.document.open();
      win.document.write('<!doctype html><title>Preparing certificates...</title><body style="font-family:Arial;padding:24px">Preparing certificate PDF...</body>');
      win.document.close();

      const pdfBlob = await authPostBlob('/api/certificates/texas-doep', {
        scheduledClassId: selectedClass.id,
        studentIds: selectedCertificateStudentIds
      });
      const pdfUrl = URL.createObjectURL(pdfBlob);
      win.location.href = pdfUrl;

      await loadRoster(selectedClass.id);
    } catch (err) {
      console.error('Print certificates error:', err);
      const message = err.message || 'Unable to print certificates';
      if (win && !win.closed) {
        win.document.open();
        win.document.write(`<!doctype html><title>Certificate PDF Error</title><body style="font-family:Arial;padding:24px"><h2>Certificate PDF Error</h2><pre style="white-space:pre-wrap;color:#b91c1c">${message.replace(/[&<>]/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[ch]))}</pre><p>Rebuild the backend so PDFKit is installed, then try again.</p></body>`);
        win.document.close();
      }
      alert(message);
    }
  };

  const certificateEligible = isCertificateEligibleClass(selectedClass);
  const selectedProgramType = getRosterProgramType(selectedClass);
  const hideTestScoreColumns = ['DWII', 'VIP'].includes(selectedProgramType);
  const hideCertificateNumberColumn = selectedProgramType === 'VIP';
  const hideDastScoreColumn = selectedProgramType === 'VIP';
  const rosterGridHeight = Math.min(
    620,
    Math.max(360, 64 + Math.max(students.length, 1) * 82 + 56)
  );

  const classRows = useMemo(
    () =>
      classes.map((item) => ({
        ...item,
        start_display: `${formatDateLabel(item.class_date)} ${formatTimeLabel(item.class_time)}`,
        end_display: `${formatDateLabel(item.end_date || item.class_date)} ${formatTimeLabel(item.end_time)}`,
        instructor_display: item.instructor_name || 'Unassigned'
      })),
    [classes]
  );

  const classColumns = [
    { field: 'class_code', headerName: 'Code', flex: 0.6 },
    { field: 'class_name', headerName: 'Class', flex: 1.4 },
    {
      field: 'status',
      headerName: 'Status',
      flex: 0.8,
      valueGetter: (_, row = {}) => String(row.status || 'open').toUpperCase()
    },
    { field: 'instructor_display', headerName: 'Instructor', flex: 1.2 },
    { field: 'start_display', headerName: 'Beginning', flex: 1.2 },
    { field: 'end_display', headerName: 'Ending', flex: 1.2 },
    { field: 'student_count', headerName: 'Students', flex: 0.7 },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 150,
      sortable: false,
      filterable: false,
      hideable: false,
      renderCell: (params) => (
        <Box display="flex" alignItems="center" height="100%">
          <Button
            size="small"
            variant="contained"
            onClick={() => loadRoster(params.row.id)}
          >
            Open
          </Button>
        </Box>
      )
    }
  ];

  const isDwiInterventionRoster = getRosterProgramType(selectedClass) === 'DWII';
  const rosterAttendanceSlots = isDwiInterventionRoster
    ? Array.from({ length: 10 }, (_, index) => ({ day: attendanceDays[index] || '', number: index + 1 }))
    : attendanceDays.map((day, index) => ({ day, number: index + 1 }));

  const rosterColumns = [
    { field: 'student_name', headerName: 'Student Name', width: 190 },
    { field: 'email', headerName: 'Email', width: 220 },
    {
      field: 'ssn_dl',
      headerName: 'DL #',
      width: 130,
      valueGetter: (_, row = {}) => row.driver_number || ''
    },
    {
      field: 'cause_number',
      headerName: 'Cause #',
      width: 190,
      renderCell: (params) => (
        <TextField
          size="small"
          value={combinedCauseNumber(params.row)}
          onChange={(e) => updateStudentLocal(params.row.student_id, { cause_number: e.target.value })}
          onBlur={() => updateStudentRecord(params.row)}
        />
      )
    },
    {
      field: 'county_of_conviction',
      headerName: 'County',
      width: 140,
      renderCell: (params) => (
        <TextField
          size="small"
          value={params.row.county_of_conviction || ''}
          onChange={(e) => updateStudentLocal(params.row.student_id, { county_of_conviction: e.target.value })}
          onBlur={() => updateStudentRecord(params.row)}
        />
      )
    },
    {
      field: 'probation_officer_name',
      headerName: 'Probation Officer',
      width: 190,
      renderCell: (params) => (
        <TextField
          size="small"
          value={params.row.probation_officer_name || ''}
          onChange={(e) => updateStudentLocal(params.row.student_id, { probation_officer_name: e.target.value })}
          onBlur={() => updateStudentRecord(params.row)}
        />
      )
    },
    {
      field: 'attorney_name',
      headerName: 'Attorney',
      width: 170,
      renderCell: (params) => (
        <TextField
          size="small"
          value={params.row.attorney_name || ''}
          onChange={(e) => updateStudentLocal(params.row.student_id, { attorney_name: e.target.value })}
          onBlur={() => updateStudentRecord(params.row)}
        />
      )
    },
    {
      field: 'referral_source',
      headerName: 'Referral Source',
      width: 180,
      renderCell: (params) => (
        <TextField
          select
          size="small"
          value={params.row.referral_source || ''}
          onChange={(e) => updateStudentLocal(params.row.student_id, { referral_source: e.target.value })}
          onBlur={() => updateStudentRecord(params.row)}
          fullWidth
        >
          <MenuItem value="">Not selected</MenuItem>
          {referralSourceOptionsFor(params.row.referral_source).map((source) => (
            <MenuItem key={source} value={source}>{source}</MenuItem>
          ))}
        </TextField>
      )
    },
    {
      field: 'birth_date',
      headerName: 'Date of Birth',
      width: 120,
      valueGetter: (_, row = {}) => formatDate(row.birth_date)
    },
    {
      field: 'reporting_agency',
      headerName: 'Reporting Agency',
      width: 180,
      renderCell: (params) => (
        <TextField
          size="small"
          value={params.row.reporting_agency || ''}
          onChange={(e) => updateStudentLocal(params.row.student_id, { reporting_agency: e.target.value })}
          onBlur={() => updateStudentRecord(params.row)}
        />
      )
    },
    ...rosterAttendanceSlots.map(({ day, number }) => ({
      field: day ? `attendance_${day}` : `dwi_attendance_slot_${number}`,
      headerName: `#${number}`,
      width: 70,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box display="flex" justifyContent="center" alignItems="center" width="100%" height="100%">
          <Checkbox
            disabled={!day || lockedWithoutOverride}
            checked={Boolean(day && params.row.attendance?.[day])}
            onChange={(e) => day && updateAttendance(params.row, day, e.target.checked)}
          />
        </Box>
      )
    })),
    {
      field: 'dast_code',
      headerName: scoreColumnLabelForClass(selectedClass),
      width: 110,
      renderCell: (params) => (
        <TextField
          size="small"
          value={params.row.dast_code || ''}
          onChange={(e) => updateStudentLocal(params.row.student_id, { dast_code: e.target.value })}
          onBlur={() => updateStudentRecord(params.row)}
        />
      )
    },
    {
      field: 'pre_test_score',
      headerName: 'Pre',
      width: 90,
      renderCell: (params) => (
        <TextField
          size="small"
          type="number"
          value={params.row.pre_test_score ?? ''}
          onChange={(e) => updateStudentLocal(params.row.student_id, { pre_test_score: e.target.value })}
          onBlur={() => updateStudentRecord(params.row)}
        />
      )
    },
    {
      field: 'post_test_score',
      headerName: 'Post',
      width: 90,
      renderCell: (params) => (
        <TextField
          size="small"
          type="number"
          value={params.row.post_test_score ?? ''}
          onChange={(e) => updateStudentLocal(params.row.student_id, { post_test_score: e.target.value })}
          onBlur={() => updateStudentRecord(params.row)}
        />
      )
    },
    {
      field: 'tx_cert_number',
      headerName: 'TX Cert#',
      width: 130,
      renderCell: (params) => (
        <TextField
          size="small"
          value={params.row.tx_cert_number || ''}
          onChange={(e) => updateStudentLocal(params.row.student_id, { tx_cert_number: e.target.value })}
          onBlur={() => updateStudentRecord(params.row)}
        />
      )
    },
    {
      field: 'amount_due',
      headerName: 'Amount Due',
      width: 120,
      renderCell: (params) => (
        <TextField
          size="small"
          type="number"
          value={params.row.amount_due ?? 0}
          onChange={(e) => updateStudentLocal(params.row.student_id, { amount_due: e.target.value })}
          onBlur={() => updateStudentRecord(params.row)}
        />
      )
    },
    {
      field: 'certificate_status',
      headerName: 'Certificate',
      width: 135,
      valueGetter: (_, row = {}) => row.certificate_printed ? `Printed${row.certificate_print_count ? ` x${row.certificate_print_count}` : ''}` : 'Not printed'
    },
    {
      field: 'edit_action',
      headerName: 'Edit Student',
      width: 130,
      sortable: false,
      filterable: false,
      hideable: false,
      renderCell: (params) => (
        <Button
          size="small"
          variant="outlined"
          startIcon={<Edit />}
          onClick={() => openStudentEditDialog(params.row)}
        >
          Edit
        </Button>
      )
    },
    {
      field: 'exit_interview_action',
      headerName: 'Exit Interview',
      width: 195,
      sortable: false,
      filterable: false,
      hideable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Button size="small" variant="outlined" onClick={() => openExitInterview(params.row)} disabled={lockedWithoutOverride}>
            {params.row.exit_interview_id ? 'Edit' : 'Start'}
          </Button>
          {params.row.exit_interview_id && (
            <Button size="small" startIcon={<PictureAsPdf />} onClick={() => printExitInterview(params.row)}>
              PDF
            </Button>
          )}
        </Stack>
      )
    },
    {
      field: 'move_action',
      headerName: 'Move To',
      width: 120,
      sortable: false,
      filterable: false,
      hideable: false,
      renderCell: (params) => (
        <Button
          size="small"
          variant="outlined"
          startIcon={<DriveFileMove />}
          disabled={lockedWithoutOverride}
          onClick={() => openTransferDialog(params.row)}
        >
          Move To
        </Button>
      )
    },
    {
      field: 'save',
      headerName: 'Save',
      width: 90,
      sortable: false,
      filterable: false,
      hideable: false,
      renderCell: (params) => (
        <Button
          size="small"
          startIcon={<Save />}
          disabled={saveAllLoading || lockedWithoutOverride}
          onClick={() => updateStudentRecord(params.row)}
        >
          Save
        </Button>
      )
    }
  ];

  const {
    columnVisibilityModel: classColumnVisibilityModel,
    setColumnVisibilityModel: setClassColumnVisibilityModel,
    setFieldVisible: setClassFieldVisible,
    showSimpleView: showClassSimpleView,
    showFullView: showClassFullView,
    resetView: resetClassView
  } = useSavedColumnView('rosters-classes', classColumns, ['class_code', 'class_name', 'instructor_display', 'start_display', 'student_count']);

  const {
    columnVisibilityModel: rosterColumnVisibilityModel,
    setColumnVisibilityModel: setRosterColumnVisibilityModel,
    setFieldVisible: setRosterFieldVisible,
    showSimpleView: showRosterSimpleView,
    showFullView: showRosterFullView,
    resetView: resetRosterView
  } = useSavedColumnView('rosters-detail', rosterColumns, ['student_name', 'email', 'ssn_dl', 'cause_number', 'birth_date', 'dast_code', 'amount_due', 'payment_total', 'balance_due', 'payment_action', 'certificate_status', 'edit_action', 'move_action']);

  const effectiveRosterColumnVisibilityModel = isDwiInterventionRoster
    ? {
      ...rosterColumnVisibilityModel,
      ...Object.fromEntries(rosterAttendanceSlots.map(({ day, number }) => [
        day ? `attendance_${day}` : `dwi_attendance_slot_${number}`,
        true,
      ])),
    }
    : rosterColumnVisibilityModel;

  const classHiddenRosterFields = new Set([
    ...(hideTestScoreColumns ? ['pre_test_score', 'post_test_score'] : []),
    ...(hideCertificateNumberColumn ? ['tx_cert_number'] : []),
    ...(hideDastScoreColumn ? ['dast_code'] : [])
  ]);
  const visibleRosterColumns = rosterColumns.filter((column) => !classHiddenRosterFields.has(column.field));

  const exportExcel = () => {
    if (!selectedClass?.id) return;

    const visibleExportColumns = exportableRosterColumns(
      rosterColumns,
      rosterColumnVisibilityModel,
      classHiddenRosterFields
    );
    const columns = dwiInterventionAttendanceColumns(
      selectedClass,
      visibleExportColumns,
      attendanceDays
    );
    if (!columns.length) {
      alert('Select at least one roster column before exporting to Excel.');
      return;
    }

    const workbook = buildRosterWorkbook(selectedClass, columns, students);
    const className = selectedClass.class_code || selectedClass.class_name || 'roster';
    const filename = `${className}-roster.xlsx`.replace(/[^a-z0-9._-]+/gi, '-');
    downloadRosterWorkbook(workbook, filename);
  };

  return (
    <Box p={3}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', md: 'center' }}
        spacing={2}
        mb={3}
      >
        <Box>
          <Typography variant="h3" gutterBottom>
            Rosters, Attendance & Grades
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage class rosters, NDP/SASSI/DAST scores, attendance, test scores, and roster exports.
          </Typography>
        </Box>
      </Stack>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Search"
              placeholder="Type multiple terms: class, student, phone, DL, cause, officer, instructor, notes..."
              value={filters.q}
              onChange={(e) => setFilters({ ...filters, q: e.target.value })}
              onKeyDown={(e) => e.key === 'Enter' && loadClasses(filters)}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              select
              fullWidth
              label="Status"
              value={filters.status}
              onChange={(e) => {
                const nextFilters = {
                  ...filters,
                  status: typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value
                };
                setFilters(nextFilters);
                loadClasses(nextFilters);
              }}
              SelectProps={{
                multiple: true,
                renderValue: (selected) => selected
                  .map((value) => STATUS_FILTER_OPTIONS.find((option) => option.value === value)?.label || value)
                  .join(', ')
              }}
            >
              {STATUS_FILTER_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  <Checkbox checked={filters.status.includes(option.value)} />
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              select
              fullWidth
              label="Class Type"
              value={filters.classType}
              onChange={(e) => setFilters({ ...filters, classType: e.target.value })}
            >
              <MenuItem value="">All scheduled class types</MenuItem>
              {filterOptions.classTypes.map((option) => (
                <MenuItem key={option.id} value={String(option.id)}>
                  {[option.code, option.name].filter(Boolean).join(' — ')} ({option.scheduled_count})
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              select
              fullWidth
              label="Instructor"
              value={filters.instructor}
              onChange={(e) => setFilters({ ...filters, instructor: e.target.value })}
            >
              <MenuItem value="">All active scheduled instructors</MenuItem>
              {filterOptions.instructors.map((option) => (
                <MenuItem key={option.id} value={String(option.id)}>
                  {[option.name, option.code].filter(Boolean).join(' — ')} ({option.scheduled_count})
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Start Date"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={filters.startDate}
              onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="End Date"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={filters.endDate}
              onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
            />
          </Grid>
          <Grid item xs={12}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }}>
              <Button variant="contained" startIcon={<Search />} onClick={() => loadClasses(filters)}>
                Search
              </Button>
              <Button
                variant="outlined"
                onClick={() => {
                  const nextFilters = defaultRosterFilters();
                  setFilters(nextFilters);
                  loadClasses(nextFilters);
                }}
              >
                Reset to Current Month Open
              </Button>
              <Button
                variant="text"
                onClick={() => {
                  const nextFilters = { ...defaultRosterFilters(), status: [], startDate: '', endDate: '' };
                  setFilters(nextFilters);
                  loadClasses(nextFilters);
                }}
              >
                Clear Filters
              </Button>
              <Typography variant="body2" color="text.secondary">
                Date range includes classes that overlap any day in the range.
              </Typography>
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 2 }}>
        <SavedColumnViewControls
          columns={classColumns}
          columnVisibilityModel={classColumnVisibilityModel}
          setFieldVisible={setClassFieldVisible}
          showSimpleView={showClassSimpleView}
          showFullView={showClassFullView}
          resetView={resetClassView}
        />
        <Box sx={{ height: 560, width: '100%' }}>
          <DataGrid
            rows={classRows}
            columns={classColumns}
            loading={loading}
            rowHeight={58}
            disableRowSelectionOnClick
            pageSizeOptions={[10, 25, 50]}
            columnVisibilityModel={classColumnVisibilityModel}
            onColumnVisibilityModelChange={setClassColumnVisibilityModel}
          />
        </Box>
      </Paper>

      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} fullWidth maxWidth="xl">
        <DialogTitle>
          {selectedClass?.class_name || 'Roster'}
        </DialogTitle>

        <DialogContent>
          {selectedClass && (
            <Stack spacing={2}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
                  <Chip label={`Class: ${selectedClass.class_code || ''} ${selectedClass.class_name || ''}`} />
                  <Chip label={`Instructor: ${selectedClass.instructor_name || 'Unassigned'}`} />
                  <Chip label={`Beginning: ${formatDateLabel(selectedClass.class_date)}`} />
                  <Chip label={`Ending: ${formatDateLabel(selectedClass.end_date || selectedClass.class_date)}`} />
                  <Chip label={`Students: ${students.length}`} />
                  <Chip color={classLocked ? 'warning' : 'success'} label={`Status: ${(selectedClass.status || 'open').toUpperCase()}`} />
                  {certificateEligible && (
                    <Chip
                      color="success"
                      label={`${selectedCertificateStudentIds.length} selected for certificate printing`}
                    />
                  )}
                </Stack>

                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }} mt={2}>
                  <Typography variant="body2" color="text.secondary">
                    Attendance days are controlled by the class schedule created on the Calendar page. Update class dates or recurrence there; this roster only records present/absent status.
                  </Typography>
                  {classLocked && (
                    <TextField
                      select
                      size="small"
                      label="Administrator Override"
                      value={adminOverride ? 'yes' : 'no'}
                      onChange={(e) => setAdminOverride(e.target.value === 'yes')}
                      sx={{ minWidth: 220 }}
                    >
                      <MenuItem value="no">Locked - no edits</MenuItem>
                      <MenuItem value="yes">Admin override</MenuItem>
                    </TextField>
                  )}
                </Stack>

                {certificateEligible && (
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }} mt={2}>
                    <Typography variant="body2" color="text.secondary">
                      PDF overlay only. Print the PDF at Actual Size / 100% on the preprinted certificate stock.
                    </Typography>
                    <Button
                      variant="outlined"
                      onClick={() => setSelectedCertificateStudentIds(students.map((student) => student.student_id))}
                    >
                      Select all students
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={() => setSelectedCertificateStudentIds([])}
                    >
                      Clear selection
                    </Button>
                    <Button
                      variant="contained"
                      color="success"
                      startIcon={<Print />}
                      onClick={printCertificates}
                      disabled={!selectedCertificateStudentIds.length}
                    >
                      Print Certificates
                    </Button>
                  </Stack>
                )}
              </Paper>

              <Stack
                direction={{ xs: 'column', md: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'stretch', md: 'center' }}
                spacing={1.5}
              >
                <SavedColumnViewControls
                  columns={visibleRosterColumns}
                  columnVisibilityModel={rosterColumnVisibilityModel}
                  setFieldVisible={setRosterFieldVisible}
                  showSimpleView={showRosterSimpleView}
                  showFullView={showRosterFullView}
                  resetView={resetRosterView}
                  label="Roster Columns"
                />
                <Button
                  variant="contained"
                  startIcon={<Save />}
                  onClick={saveAllStudentRecords}
                  disabled={!students.length || detailLoading || saveAllLoading || lockedWithoutOverride}
                >
                  {saveAllLoading ? 'Saving All...' : 'Save All'}
                </Button>
              </Stack>
              <Box
                sx={{
                  height: rosterGridHeight,
                  maxHeight: '58vh',
                  minHeight: 360,
                  width: '100%'
                }}
              >
                <DataGrid
                    rows={students.map((student) => ({ ...student, id: student.student_id }))}
                    columns={visibleRosterColumns}
                    loading={detailLoading}
                    rowHeight={82}
                    columnHeaderHeight={64}
                    checkboxSelection={certificateEligible}
                    rowSelectionModel={certificateEligible ? selectedCertificateStudentIds : []}
                    onRowSelectionModelChange={(newSelection) => {
                      if (certificateEligible) setSelectedCertificateStudentIds(Array.from(newSelection));
                    }}
                    disableRowSelectionOnClick
                    pageSizeOptions={[10, 25, 50]}
                    columnVisibilityModel={effectiveRosterColumnVisibilityModel}
                    onColumnVisibilityModelChange={setRosterColumnVisibilityModel}
                    sx={{
                        '& .MuiDataGrid-cell': {
                        display: 'flex',
                        alignItems: 'center'
                        },
                        '& .MuiDataGrid-columnHeader': {
                        display: 'flex',
                        alignItems: 'center'
                        },
                        '& .MuiInputBase-root': {
                        height: 44
                        }
                    }}
                />
              </Box>
            </Stack>
          )}
        </DialogContent>

        <DialogActions>
          <Button startIcon={<PictureAsPdf />} variant="outlined" onClick={exportPdf}>
            Export Roster PDF
          </Button>
          <Button startIcon={<TableView />} variant="outlined" onClick={exportExcel}>
            Export Roster Excel
          </Button>
          <Button onClick={() => setDetailOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(exitInterviewDialog)} onClose={() => setExitInterviewDialog(null)} maxWidth="md" fullWidth>
        <DialogTitle>Exit Interview — {exitInterviewDialog?.student_name}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} md={6}><TextField fullWidth label="Program" value={exitInterviewForm.exit_interview_program} onChange={(e) => setExitInterviewForm({ ...exitInterviewForm, exit_interview_program: e.target.value })} /></Grid>
            <Grid item xs={12} md={6}><TextField fullWidth type="date" InputLabelProps={{ shrink: true }} label="Completion Date" value={dateOnly(exitInterviewForm.exit_interview_completion_date)} onChange={(e) => setExitInterviewForm({ ...exitInterviewForm, exit_interview_completion_date: e.target.value })} /></Grid>
            <Grid item xs={12} md={6}><TextField fullWidth label="Participation Rating" value={exitInterviewForm.exit_interview_participation_rating} onChange={(e) => setExitInterviewForm({ ...exitInterviewForm, exit_interview_participation_rating: e.target.value })} /></Grid>
            <Grid item xs={12} md={6}><TextField fullWidth label="Instructor" value={exitInterviewForm.exit_interview_instructor} onChange={(e) => setExitInterviewForm({ ...exitInterviewForm, exit_interview_instructor: e.target.value })} /></Grid>
            <Grid item xs={12}><TextField fullWidth multiline minRows={2} label="Recommendations" placeholder="Enter recommendations to print" helperText="Optional — prints only what you enter and save." value={exitInterviewForm.exit_interview_recommendations} onChange={(e) => setExitInterviewForm({ ...exitInterviewForm, exit_interview_recommendations: e.target.value })} /></Grid>
            <Grid item xs={12} md={6}><TextField fullWidth label="Evaluation Instrument" value={exitInterviewForm.exit_interview_evaluation_instrument} onChange={(e) => setExitInterviewForm({ ...exitInterviewForm, exit_interview_evaluation_instrument: e.target.value })} /></Grid>
            <Grid item xs={12} md={6}><TextField fullWidth label="Evaluation Result" value={exitInterviewForm.exit_interview_evaluation_result} onChange={(e) => setExitInterviewForm({ ...exitInterviewForm, exit_interview_evaluation_result: e.target.value })} /></Grid>
            <Grid item xs={12}><TextField fullWidth multiline minRows={2} label="Evaluation Narrative" placeholder="Enter the evaluation narrative to print" helperText="Optional — prints only what you enter and save." value={exitInterviewForm.exit_interview_evaluation_narrative} onChange={(e) => setExitInterviewForm({ ...exitInterviewForm, exit_interview_evaluation_narrative: e.target.value })} /></Grid>
            <Grid item xs={12} md={6}><TextField fullWidth label="Program Certification #" value={exitInterviewForm.exit_interview_program_certification_number} onChange={(e) => setExitInterviewForm({ ...exitInterviewForm, exit_interview_program_certification_number: e.target.value })} /></Grid>
            <Grid item xs={12} md={6}><TextField fullWidth type="date" InputLabelProps={{ shrink: true }} label="Print Date" value={dateOnly(exitInterviewForm.exit_interview_print_date)} onChange={(e) => setExitInterviewForm({ ...exitInterviewForm, exit_interview_print_date: e.target.value })} /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions><Button onClick={() => setExitInterviewDialog(null)}>Cancel</Button><Button variant="contained" onClick={saveExitInterview} disabled={exitInterviewLoading}>Save Exit Interview</Button></DialogActions>
      </Dialog>

      <Dialog open={Boolean(transferDialog)} onClose={() => setTransferDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Move Student To</DialogTitle>
        <DialogContent>
          {transferDialog && (
            <Stack spacing={2} mt={0.5}>
              <Typography variant="body2" color="text.secondary">
                Moving {transferDialog.studentName} from {transferDialog.sourceClassName}. Previous roster, attendance, and payment history remains attached to the student.
              </Typography>
              <TextField
                select
                fullWidth
                label="Destination"
                value={transferDialog.destination_type}
                onChange={(e) => setTransferDialog({
                  ...transferDialog,
                  destination_type: e.target.value,
                  destination_class_id: ''
                })}
              >
                <MenuItem value="class">Different Class</MenuItem>
                <MenuItem value="holding_tank">Holding Tank</MenuItem>
                <MenuItem value="future_class">Future Class</MenuItem>
              </TextField>
              <TextField
                select
                required
                fullWidth
                label={transferDialog.destination_type === 'holding_tank' ? 'Holding Tank Class' : 'Destination Class'}
                helperText={transferDialog.destination_type === 'holding_tank'
                  ? 'Showing existing classes with “holding” or “tank” in the class name/code.'
                  : ''}
                value={transferDialog.destination_class_id}
                onChange={(e) => setTransferDialog({ ...transferDialog, destination_class_id: e.target.value })}
              >
                {transferDestinationClasses.length === 0 && (
                  <MenuItem value="" disabled>
                    {transferDialog.destination_type === 'holding_tank'
                      ? 'No holding tank classes found'
                      : 'No destination classes found'}
                  </MenuItem>
                )}
                {transferDestinationClasses.map((item) => (
                  <MenuItem key={item.id} value={item.id}>
                    {formatDate(item.class_date)}{item.end_date && dateOnly(item.end_date) !== dateOnly(item.class_date) ? `–${formatDate(item.end_date)}` : ''} — {item.class_name} {item.class_time ? `@ ${String(item.class_time).slice(0, 5)}` : ''}{item.instructor_name ? ` — ${item.instructor_name}` : ''} ({Number(item.student_count || 0)} students)
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                fullWidth
                multiline
                minRows={2}
                label="Transfer Notes"
                value={transferDialog.notes}
                onChange={(e) => setTransferDialog({ ...transferDialog, notes: e.target.value })}
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTransferDialog(null)}>Cancel</Button>
          <Button variant="contained" onClick={submitTransfer}>Move</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(studentEditDialog)} onClose={() => setStudentEditDialog(null)} maxWidth="md" fullWidth>
        <DialogTitle>Edit Student From Roster</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={0.5}>
            <Typography variant="body2" color="text.secondary">
              Update the student's main record without leaving the roster. Roster-only fields like grades, attendance, amount due, and certificate number still save in the grid.
            </Typography>
            <Divider />
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}><TextField fullWidth label="First Name" value={studentEditForm.first_name} onChange={(e) => setStudentEditForm({ ...studentEditForm, first_name: e.target.value })} /></Grid>
              <Grid item xs={12} md={4}><TextField fullWidth label="Middle Name" value={studentEditForm.middle_name} onChange={(e) => setStudentEditForm({ ...studentEditForm, middle_name: e.target.value })} /></Grid>
              <Grid item xs={12} md={4}><TextField fullWidth label="Last Name" value={studentEditForm.last_name} onChange={(e) => setStudentEditForm({ ...studentEditForm, last_name: e.target.value })} /></Grid>
              <Grid item xs={12} md={3}><TextField fullWidth label="DOB" type="date" InputLabelProps={{ shrink: true }} value={studentEditForm.birth_date} onChange={(e) => setStudentEditForm({ ...studentEditForm, birth_date: e.target.value })} /></Grid>
              <Grid item xs={12} md={3}><TextField fullWidth label="SSN Last 4" inputProps={{ maxLength: 4 }} value={studentEditForm.ssn} onChange={(e) => setStudentEditForm({ ...studentEditForm, ssn: e.target.value.replace(/\D/g, '').slice(0, 4) })} /></Grid>
              <Grid item xs={12} md={3}><TextField fullWidth label="Phone" value={studentEditForm.phone} onChange={(e) => setStudentEditForm({ ...studentEditForm, phone: formatPhone(e.target.value) })} /></Grid>
              <Grid item xs={12} md={3}><TextField fullWidth label="Email" value={studentEditForm.email} onChange={(e) => setStudentEditForm({ ...studentEditForm, email: e.target.value })} /></Grid>
              <Grid item xs={12} md={6}><TextField fullWidth label="Address" value={studentEditForm.address} onChange={(e) => setStudentEditForm({ ...studentEditForm, address: e.target.value })} /></Grid>
              <Grid item xs={12} md={2}><TextField fullWidth label="City" value={studentEditForm.city} onChange={(e) => setStudentEditForm({ ...studentEditForm, city: e.target.value })} /></Grid>
              <Grid item xs={12} md={2}><TextField fullWidth label="State" value={studentEditForm.state} onChange={(e) => setStudentEditForm({ ...studentEditForm, state: e.target.value })} /></Grid>
              <Grid item xs={12} md={2}><TextField fullWidth label="Zip" value={studentEditForm.zip} onChange={(e) => setStudentEditForm({ ...studentEditForm, zip: e.target.value })} /></Grid>
              <Grid item xs={12} md={4}><TextField fullWidth label="Driver Number" value={studentEditForm.driver_number} onChange={(e) => setStudentEditForm({ ...studentEditForm, driver_number: e.target.value })} /></Grid>
              <Grid item xs={12} md={8}><TextField fullWidth label="Cause Number" value={studentEditForm.cause_number} onChange={(e) => setStudentEditForm({ ...studentEditForm, cause_number: e.target.value })} /></Grid>
              <Grid item xs={12}><TextField fullWidth label="County of Conviction" value={studentEditForm.county_of_conviction} onChange={(e) => setStudentEditForm({ ...studentEditForm, county_of_conviction: e.target.value })} /></Grid>
              <Grid item xs={12} md={4}><TextField fullWidth label="Probation Officer" value={studentEditForm.probation_officer_name} onChange={(e) => setStudentEditForm({ ...studentEditForm, probation_officer_name: e.target.value })} /></Grid>
              <Grid item xs={12} md={4}><TextField fullWidth label="Probation Email" value={studentEditForm.probation_officer_email} onChange={(e) => setStudentEditForm({ ...studentEditForm, probation_officer_email: e.target.value })} /></Grid>
              <Grid item xs={12} md={4}><TextField fullWidth label="Probation Phone" value={studentEditForm.probation_officer_phone} onChange={(e) => setStudentEditForm({ ...studentEditForm, probation_officer_phone: formatPhone(e.target.value) })} /></Grid>
              <Grid item xs={12} md={4}><TextField fullWidth label="Attorney" value={studentEditForm.attorney_name} onChange={(e) => setStudentEditForm({ ...studentEditForm, attorney_name: e.target.value })} /></Grid>
              <Grid item xs={12} md={4}><TextField fullWidth label="Attorney Email" value={studentEditForm.attorney_email} onChange={(e) => setStudentEditForm({ ...studentEditForm, attorney_email: e.target.value })} /></Grid>
              <Grid item xs={12} md={4}><TextField fullWidth label="Attorney Phone" value={studentEditForm.attorney_phone} onChange={(e) => setStudentEditForm({ ...studentEditForm, attorney_phone: formatPhone(e.target.value) })} /></Grid>
              <Grid item xs={12}>
                <TextField select fullWidth label="Referral Source" value={studentEditForm.referral_source} onChange={(e) => setStudentEditForm({ ...studentEditForm, referral_source: e.target.value })}>
                  <MenuItem value="">Not selected</MenuItem>
                  {referralSourceOptionsFor(studentEditForm.referral_source).map((source) => (
                    <MenuItem key={source} value={source}>{source}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12}><TextField fullWidth multiline minRows={2} label="Additional State Reporting Fields" value={studentEditForm.state_reporting_notes} onChange={(e) => setStudentEditForm({ ...studentEditForm, state_reporting_notes: e.target.value })} /></Grid>
              <Grid item xs={12}><TextField fullWidth label="Reporting Agency" value={studentEditForm.reporting_agency} onChange={(e) => setStudentEditForm({ ...studentEditForm, reporting_agency: e.target.value })} /></Grid>
              <Grid item xs={12}><TextField fullWidth multiline minRows={2} label="Call Notes" value={studentEditForm.call_notes} onChange={(e) => setStudentEditForm({ ...studentEditForm, call_notes: e.target.value })} /></Grid>
            </Grid>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStudentEditDialog(null)}>Cancel</Button>
          <Button variant="contained" startIcon={<Save />} onClick={saveStudentEdit} disabled={studentEditLoading}>
            Save Student
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
